import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import MetaFormInline from 'Components/UI/Meta/MetaForm/Components/MetaFormInline';
import fr from 'facet-rest';
import classNames from 'classnames';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { noop } from 'Helpers/PropTypes';
import Layout from './Layout';

const ENTER_KEYCODE = 13;

const styles = () => ({
  root: {},
});

const operatorName = (queryName) => `${queryName}Operator`;
const operatorPrompt = (queryPrompt) => `${queryPrompt} Operator`;

export const buildResourceFromQueryMeta = (collection, queries) => {
  // Get queryParams off of collection self link
  const collectionQueryLinkParams = new URL(collection.link('self').url()).searchParams;

  // Start to build attributes object
  const attributes = {};
  // Copy the meta object
  const meta = JSON.parse(JSON.stringify(queries));
  // For each query field
  queries.template.forEach(query => {
    // add to template
    if (query.operators && query.operators.length > 0) {
      const index = meta.template.findIndex(template => {
        return template.name === query.name;
      });

      meta.template.splice(index + 1, 0, {
        name: operatorName(query.name),
        prompt: operatorPrompt(query.prompt),
        options: query.operators,
        type: 'picklist',
        hidden: true,
      });
    }

    // add to attributes
    // Get the value out of the query params
    const queryParam = collectionQueryLinkParams.get(query.name);
    if (queryParam === null) {
      return;
    }

    // Set the attribute value of the query param to the grabbed value
    attributes[query.name] = queryParam;

    if (queryParam && queryParam.includes('|')) {
      [attributes[operatorName(query.name)], attributes[query.name]] = queryParam.split('|');
    }

    // If the query type is a multipick list, and the queryParam has data to insert, format it as an array
    if (query.type === 'multipicklist' && queryParam) {
      attributes[query.name] = queryParam.split(',');
    }
  });

  return fr.resource({
    attributes,
    meta,
  });
};

/**
 * @description A search Query form that provides all or the specified query
 * parameters of a collection to modify and submit a query. You can define which query fields are available to the search form by
 * passing in an array of field names to the queryFields prop. If you would like to have a custom layout for the
 * search form, you can pass in a layout component to the layout prop.
 *
 * @summary A search Query form. Requires CollectionQueryProvider
 *
 * @param {object} props - Component props
 * @param {object} metaOverrideSet - *optional* A metaOverrideSet. Set fields that you don't want displayed to hidden.
 * @param {JSX.Element} layout - *optional* A layout component used to define the layout of the search form
 * @param {Collection} collection - The collection being queried
 * @param {function} onSubmit - The onSubmit function
 * @return {JSX.Element} CollectionQueryForm
 */
const CollectionQueryForm = ({
  component,
  className,
  classes,
  metaOverrideSet,
  layout,
  LayoutProps,
  collection,
  beforeSubmit,
  onSubmit,
  formPadding,
  formMargin,
  removeEmptyQueryParams,
}) => {
  const [loading, setLoading] = useState(false);
  const [prompt, setPrompt] = useState(undefined);
  const [queryFormResource, setQueryFormResource] = React.useState(null);
  const { displayErrorMessage } = useSnackbarContext();

  const addToLink = (link, attr) => {
    const operator = queryFormResource.attr(operatorName(attr)) || 'eq';
    const value = queryFormResource.attr(attr);
    link.queryParamFilter(attr, value, operator);
  };

  const handleSubmit = async (value) => {
    beforeSubmit(value);
    setLoading(true);
    try {
      const resp = await collection.query(collection.meta().queries().head().attr('rel'), (link) => {
        // Copy the link
        const linkCopy = link.copy()
          .withQueryParams();


        // Loop through the resource attributes and apply values to the link
        Object.keys(queryFormResource.attributes()).forEach(attr => {
          if (queryFormResource.attr(attr) && !attr.includes('Operator')) {
            // If it is an empty array, do not set it on the link unless removeEmptyQueryParams is set
            if (!Array.isArray(queryFormResource.attr(attr)) || !(queryFormResource.attr(attr).length === 0)) {
              addToLink(linkCopy, attr);
              return;
            }
            if (removeEmptyQueryParams) {
              linkCopy.removeSearchParam(attr);
            }
          }
        });
        // Fetch built link
        return linkCopy.get();
      });

      onSubmit(resp, value);
    } catch (e) {
      if (e.errors) {
        displayErrorMessage(e, e.errors);
      } else {
        displayErrorMessage(e, 'Error: Failed to submit form');
      }
    }
    setLoading(false);

    return null;
  };

  const handleEnterKeyPress = async (e) => {
    if (e.which === ENTER_KEYCODE) {
      await handleSubmit();
    }
  };

  useEffect(() => {
    // Get collection queries meta
    const queriesMeta = collection?.meta()?.queries()?.head()?.export();
    if (!queriesMeta) {
      return;
    }

    // Build the resources object with search params
    const resource = buildResourceFromQueryMeta(collection, queriesMeta);

    // Set the state to built resource
    setQueryFormResource(resource);
    setPrompt(queriesMeta.prompt);
  }, [collection]);

  // If no queryFormResource is set, return nothing
  if (!queryFormResource) {
    return null;
  }
  return (
    <MetaFormInline
      component={component}
      className={classNames(className, classes.root)}
      resource={queryFormResource}
      layout={layout}
      padding={formPadding}
      margin={formMargin}
      LayoutProps={{
        collection,
        onSubmit: handleSubmit,
        loading,
        prompt,
        ...LayoutProps,
      }}
      noIndividualSubmitButtons
      metaOverrideSet={metaOverrideSet}
      onKeyPress={handleEnterKeyPress}
    />
  );
};

CollectionQueryForm.defaultProps = {
  component: undefined,
  className: '',
  metaOverrideSet: undefined,
  formPadding: 'default',
  formMargin: 'default',
  layout: Layout,
  LayoutProps: {},
  beforeSubmit: noop,
  removeEmptyQueryParams: false,
};

CollectionQueryForm.propTypes = {
  component: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  className: PropTypes.string,
  formPadding: PropTypes.string,
  formMargin: PropTypes.string,
  classes: PropTypes.object.isRequired,
  LayoutProps: PropTypes.object,
  metaOverrideSet: PropTypes.object,
  layout: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
  collection: PropTypes.instanceOf(fr.Collection).isRequired,
  beforeSubmit: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  removeEmptyQueryParams: PropTypes.bool,
};

export default withStyles(styles)(CollectionQueryForm);
